import React, { useEffect, useState, useContext, useRef, ReactNode } from "react";
import { connect } from "react-redux";
import Card from "@mui/material/Card";
import MSplitter from "components/Shared/MSplitter.tsx";
import MGrid, { ACTION_ADD } from "components/Shared/MGrid.tsx";
import { getSearchParameter } from "components/Shared/Functions";
import { searchCustomersText, getCustomerDetails } from "actions/customer";
import { DATA_TYPE_DATE, DATA_TYPE_NUMBER_UNFORMATTED } from "typeConstants/dataTypes";
import { userHasRight } from "selectors/rights";
import { CUSTOMER_ACCOUNT_CREATE, CUSTOMER_DETAILS_VIEW } from "typeConstants/rights";
import { CustomerListModel, CustomerAddRequest } from "interfaces/Mueller.Business.Models.ts";
import { withRouter } from "tools/routerCompat";
import { RouteProps } from "react-router";
import { DialogContext } from "components/Shared/DialogProvider.tsx";
import AddCustomer from "../Add/AddCustomer.tsx";
import { addCustomer } from "requests/customer";

interface CustomerListProps {
  splitComponent: ReactNode;
  splitComponentTitle: string;
  // Router
  location: RouteProps["location"];
  history: any;
  // Redux
  customerList: CustomerListModel[];
  fetchingCustomerList: boolean;
  fetchingCustomerDetails: boolean;
  searchCustomersText: (searchTerm: String) => void;
  getCustomerDetails: (customerNumber: number) => void;
  canAddCustomer: boolean;
  canViewCustomerDetails: boolean;
}

/**
 * This component searches for a customer (data passed from maintenance card).
 * It will render the component passed in through the splitComponent prop when a customer is selected.
 * The splitComponent will need to watch these redux props:
 *    state.customer.customerDetails
 *    state.customer.fetchingCustomerDetails
 */

const CustomerList = (props: CustomerListProps) => {
  const [selectedCustomer, setSelectedCustomer] = useState<number>();
  const dialog = useContext(DialogContext);
  const mounted = useRef(true);

  useEffect(() => {
    props.searchCustomersText(getSearchParameter(props.location));
    return () => {
      mounted.current = false;
    };
  }, []);

  const columns = [
    { key: "accountNumber", name: "Number", dataSource: "number", width: 80, dataType: DATA_TYPE_NUMBER_UNFORMATTED },
    { key: "accountName", name: "Name", dataSource: "displayName" },
    { key: "companyName", name: "Company", dataSource: "majorCompany.companyName" },
    { key: "salesRepName", name: "Sales Rep", width: 175, dataSource: "salesRepName" },
    { key: "qaDirector", name: "QA Director", dataSource: row => `${row.qaDirector?.map(qad => qad.firstLastNameOnly).join(", ")}` },
    { key: "customerExperienceDirector", name: "Customer Experience Director", dataSource: row => row.customerExperienceDirector.map(l => `${l.firstName} ${l.lastName}`).join(", ") },
    { key: "inactiveDate", dataType: DATA_TYPE_DATE, name: "Inactive Date", width: 100, dataSource: "inactiveDate" },
  ];

  const onRowClick = (index: string | number) => {
    let selected = props.customerList[index].number;
    props.getCustomerDetails(selected);
    setSelectedCustomer(selected);
  };

  const clearCustomer = () => setSelectedCustomer(undefined);

  const onSubmit = (request: CustomerAddRequest) => {
    addCustomer(
      request.customerNumber,
      request,
      // If successful navigate to new customer
      () => { dialog.hide(); props.searchCustomersText(request.companyName); },
      () => dialog.hide(),
      mounted
    );
  };

  const onAdd = () => {
    const dialogProps = { className: "add-customer-dialog" };
    dialog.show(
      "Add Customer Account",
      <AddCustomer onCancel={() => dialog.hide()} onSubmit={onSubmit} />,
      null,
      dialogProps);
  };

  let actions = [];
  if (props.canAddCustomer) {
    actions.push({ code: ACTION_ADD, onClick: onAdd });
  }

  return (
    <MSplitter
      firstChild={
        <div className="padding-page-content full-height">
          <Card className="full-height">
            <MGrid
              headerText="Accounts"
              columnDefinitions={columns}
              data={props.customerList}
              isFetchingData={props.fetchingCustomerList}
              onRowClick={props.canViewCustomerDetails ? onRowClick : null}
              displayNumOfItemsFound={true}
              actions={actions}
            />
          </Card>
        </div>}
      secondChild={selectedCustomer && props.splitComponent}
      secondChildTitle={{
        title: props.splitComponentTitle,
        buttons: [
          { icon: "fullscreen", onClick: () => { props.history.push("/customer/details"); } },
          { icon: "close", onClick: clearCustomer },
        ]
      }}

    />
  );
};

const mapStateToProps = state => ({
  customerList: state.customer.customerList,
  fetchingCustomerList: state.customer.fetchingCustomerList,
  fetchingCustomerDetails: state.customer.fetchingCustomerDetails,
  canAddCustomer: userHasRight(CUSTOMER_ACCOUNT_CREATE)(state),
  canViewCustomerDetails: userHasRight(CUSTOMER_DETAILS_VIEW)(state),
});

const mapDispatchToProps = dispatch => ({
  searchCustomersText(searchTerm: String) {
    dispatch(searchCustomersText(searchTerm));
  },
  getCustomerDetails(customerNumber: number) {
    dispatch(getCustomerDetails(customerNumber));
  },
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(CustomerList));
